import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Responsive, WidthProvider } from 'react-grid-layout'
import { PlusIcon, TrashIcon } from 'lucide-react'
import { EditIcon, SaveIcon, XIcon, EyeIcon, EyeOffIcon } from './Icons'
import { FlowLogoLoading } from './FlowLogo'
import { API_BASE_URL } from '../utils/api'
import { AVAILABLE_WIDGETS, getWidgetComponent, getWidgetConfig } from './widgets/WidgetRegistry'
import { DesignTheme } from './widgets/FinancialWidgetDesignSelector'
import { DashboardProvider } from '../contexts/DashboardContext'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

// Add custom styles for the grid layout
const gridStyles = `
  .react-grid-layout {
    position: relative;
    min-height: 400px;
  }
  .react-grid-item {
    transition: all 200ms ease;
    transition-property: left, top;
  }
  .react-grid-item.cssTransforms {
    transition-property: transform;
  }
  .react-grid-item > .react-resizable-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    bottom: 0;
    right: 0;
    background: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNiIgaGVpZ2h0PSI2IiB2aWV3Qm94PSIwIDAgNiA2IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8ZG90cyBmaWxsPSIjOTk5Ij4KPGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiLz4KPGNpcmNsZSBjeD0iMSIgY3k9IjMiIHI9IjEiLz4KPGNpcmNsZSBjeD0iMSIgY3k9IjUiIHI9IjEiLz4KPGNpcmNsZSBjeD0iMyIgY3k9IjMiIHI9IjEiLz4KPGNpcmNsZSBjeD0iMyIgY3k9IjUiIHI9IjEiLz4KPGNpcmNsZSBjeD0iNSIgY3k9IjUiIHI9IjEiLz4KPC9kb3RzPgo8L3N2Zz4K');
    background-position: bottom right;
    padding: 0 3px 3px 0;
    background-repeat: no-repeat;
    background-origin: content-box;
    box-sizing: border-box;
    cursor: se-resize;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = gridStyles;
  document.head.appendChild(styleElement);
}

const ResponsiveGridLayout = WidthProvider(Responsive)

// Layout item for react-grid-layout
interface LayoutItem {
  i: string
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
  maxW?: number
  maxH?: number
  isDraggable?: boolean
  isResizable?: boolean
}

interface DashboardConfig {
  id: number
  name: string
  layout: LayoutItem[]
}

interface DashboardGridProps {
  userId: number
  designTheme?: DesignTheme
}

const DashboardGrid: React.FC<DashboardGridProps> = ({ userId, designTheme = 'minimal' }) => {
  const { t } = useTranslation()
  const [isEditMode, setIsEditMode] = useState(false)
  const [layout, setLayout] = useState<LayoutItem[]>([])
  const [originalLayout, setOriginalLayout] = useState<LayoutItem[]>([])
  const [loading, setLoading] = useState(true)
  const [isPrivacyMode, setIsPrivacyMode] = useState(false)
  const [widgetParameters, setWidgetParameters] = useState<{ [widgetId: string]: any }>({})
  // Increment to force DashboardProvider re-mount when widgetParameters change after config load
  const [contextKey, setContextKey] = useState(0)

  // Financial widget types that should be blurred in privacy mode
  const financialWidgets = ['pipeline', 'funded', 'open_revenue', 'paid', 'pipeline_value', 'average_bps', 'yearly_statistics', 'lender_metrics']

  // Default layout for new users - Mobile Optimized
  const getDefaultLayout = (): LayoutItem[] => [
    {
      i: 'pipeline_value',
      x: 0,
      y: 0,
      w: 2,
      h: 2,
      minW: 1, // Allow mobile to use 1 column
      minH: 2,
      maxW: 3,
      maxH: 3,
    },
    {
      i: 'whats_new',
      x: 2,
      y: 0,
      w: 1,
      h: 2,
      minW: 1,
      minH: 1, // Allow smaller height on mobile
      maxW: 2,
      maxH: 3,
    },
    {
      i: 'pipeline',
      x: 0,
      y: 2,
      w: 1,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 2,
      maxH: 2,
    },
    {
      i: 'funded',
      x: 1,
      y: 2,
      w: 1,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 2,
      maxH: 2,
    },
    {
      i: 'open_revenue',
      x: 2,
      y: 2,
      w: 1,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 2,
      maxH: 2,
    },
    {
      i: 'paid',
      x: 0,
      y: 3,
      w: 1,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 2,
      maxH: 2,
    },
    {
      i: 'task_overview',
      x: 1,
      y: 3,
      w: 1,
      h: 1,
      minW: 1,
      minH: 1,
      maxW: 2,
      maxH: 2,
    },
  ]

  // Load dashboard configuration
  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setLayout(getDefaultLayout())
        setLoading(false)
        return
      }

      // Load from database
      const response = await fetch(`${API_BASE_URL}/api/dashboard-config?layout=default`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const config = await response.json()
        
        // Convert database widget format to layout format and extract parameters
        const layoutItems: LayoutItem[] = config.widgets.map((widget: any) => ({
          i: widget.id,
          x: widget.x,
          y: widget.y,
          w: widget.w,
          h: widget.h,
          minW: widget.minW,
          minH: widget.minH,
          maxW: widget.maxW,
          maxH: widget.maxH,
        }))
        
        // Extract widget parameters
        const parameters: { [widgetId: string]: any } = {}
        config.widgets.forEach((widget: any) => {
          if (widget.parameters) {
            parameters[widget.id] = widget.parameters
          }
        })
        
        setLayout(layoutItems)
        setWidgetParameters(parameters)
        // Sync to context (the provider re-renders with the new initialWidgetParameters
        // only on next render, so we store in state and pass down via key prop)
        setContextKey(ck => ck + 1)
      } else {
        setLayout(getDefaultLayout())
      }

      // Load privacy mode preference from localStorage for now
      const savedPrivacyMode = localStorage.getItem('dashboard_privacy_mode')
      if (savedPrivacyMode) {
        setIsPrivacyMode(JSON.parse(savedPrivacyMode))
      }
    } catch (error) {
      console.error('Error loading dashboard config:', error)
      setLayout(getDefaultLayout())
    } finally {
      setLoading(false)
    }
  }

  // Save dashboard configuration
  const saveConfig = async (newLayout: LayoutItem[]) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        console.error('No auth token, cannot save dashboard config')
        return
      }

      // Convert layout format to database widget format
      const widgets = newLayout.map((item) => ({
        id: item.i,
        type: item.i.replace(/_\d+$/, ''), // Extract widget type
        x: item.x,
        y: item.y,
        w: item.w,
        h: item.h,
        minW: item.minW,
        minH: item.minH,
        maxW: item.maxW,
        maxH: item.maxH,
        // Include current widget parameters
        parameters: widgetParameters[item.i] || {}
      }))

      const response = await fetch(`${API_BASE_URL}/api/dashboard-config`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          layout_name: 'default',
          widgets,
          grid_cols: 3,
          grid_rows: 6
        }),
      })

      if (!response.ok) {
        console.error('Failed to save dashboard config')
      }
    } catch (error) {
      console.error('Error saving dashboard config:', error)
    }
  }

  useEffect(() => {
    loadConfig()
  }, [userId])

  const handleLayoutChange = (newLayout: LayoutItem[]) => {
    if (isEditMode) {
      setLayout(newLayout)
    }
  }

  const handleEditMode = () => {
    setOriginalLayout([...layout])
    setIsEditMode(true)
  }

  const handleSave = async () => {
    await saveConfig(layout)
    setIsEditMode(false)
    setOriginalLayout([])
  }

  const handleCancel = () => {
    setLayout([...originalLayout])
    setIsEditMode(false)
    setOriginalLayout([])
  }

  const handleRemoveWidget = (widgetId: string) => {
    const newLayout = layout.filter(item => item.i !== widgetId)
    setLayout(newLayout)
  }

  const handleAddWidget = (widgetId: string) => {
    const widgetConfig = getWidgetConfig(widgetId)
    if (!widgetConfig) return

    // Check if widget already exists
    const widgetExists = layout.some(item => {
      const widgetType = item.i.replace(/_\d+$/, '')
      return widgetType === widgetId
    })

    if (widgetExists) {
      return
    }

    // Find an available position
    const maxY = Math.max(...layout.map(item => item.y + item.h), 0)
    
    const newItem: LayoutItem = {
      i: `${widgetId}_${Date.now()}`, // Make unique
      x: 0,
      y: maxY,
      w: widgetConfig.defaultWidth, // Use default width (not 100%)
      h: widgetConfig.maxHeight || widgetConfig.defaultHeight, // Always add new widgets at 100% height (maxHeight)
      minW: widgetConfig.minWidth,
      minH: widgetConfig.minHeight,
      maxW: widgetConfig.maxWidth,
      maxH: widgetConfig.maxHeight,
    }

    setLayout([...layout, newItem])
  }

  // Helper function to check if a widget type is already added
  const isWidgetAdded = (widgetId: string): boolean => {
    return layout.some(item => {
      const widgetType = item.i.replace(/_\d+$/, '')
      return widgetType === widgetId
    })
  }

  const handleTogglePrivacyMode = () => {
    const newPrivacyMode = !isPrivacyMode
    setIsPrivacyMode(newPrivacyMode)
    
    // Save to localStorage
    try {
      localStorage.setItem('dashboard_privacy_mode', JSON.stringify(newPrivacyMode))
    } catch (error) {
      console.error('Error saving privacy mode:', error)
    }
  }

  const renderWidget = (item: LayoutItem) => {
    // Extract widget type from item.i (remove any timestamp suffix)
    const widgetType = item.i.replace(/_\d+$/, '')
    const WidgetComponent = getWidgetComponent(widgetType)
    const isFinancialWidget = financialWidgets.includes(widgetType)
    const shouldBlur = isPrivacyMode && isFinancialWidget
    
    if (!WidgetComponent) {
      return (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 flex items-center justify-center h-full">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm">Unknown widget: {widgetType}</p>
            <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">ID: {item.i}</p>
          </div>
        </div>
      )
    }

    return (
      <div className={`w-full h-full relative ${shouldBlur ? 'select-none' : ''}`}>
        <div className={`w-full h-full transition-all duration-300 ${shouldBlur ? 'blur-md' : ''}`}>
          <WidgetComponent 
            className="w-full h-full" 
            designTheme={designTheme} 
            widgetId={item.i} 
          />
        </div>
        {shouldBlur && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-xl">
            <div className="text-center p-4">
              <EyeOffIcon size={24} className="text-gray-600 dark:text-gray-400 mx-auto mb-2 opacity-70" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium opacity-70">
                Private Mode
              </p>
            </div>
          </div>
        )}
      </div>
    )
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FlowLogoLoading />
      </div>
    )
  }

  return (
    <DashboardProvider key={contextKey} initialWidgetParameters={widgetParameters}>
      <div className="dashboard-grid-container relative min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] w-full overflow-x-hidden">

        {/* Add Widget Panel (only in edit mode) - Mobile Optimized */}
        {isEditMode && (
          <div className="mb-4 sm:mb-6 space-y-4">
            {/* Available Widgets Section */}
            {AVAILABLE_WIDGETS.filter(widget => !isWidgetAdded(widget.id)).length > 0 && (
              <div className="p-3 sm:p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">{t('dashboard.widgets.addWidgets')}</h3>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  {AVAILABLE_WIDGETS.filter(widget => !isWidgetAdded(widget.id)).map((widget) => (
                    <button
                      key={widget.id}
                      onClick={() => handleAddWidget(widget.id)}
                      className="flex items-center justify-center sm:justify-start space-x-1 sm:space-x-2 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 sm:px-3 py-2 sm:py-2 rounded-lg text-xs sm:text-sm transition-colors min-h-[40px] touch-manipulation"
                    >
                      <PlusIcon size={12} className="sm:w-[14px] sm:h-[14px] flex-shrink-0" />
                      <span className="truncate text-center sm:text-left">{t(`dashboard.widgets.add${widget.id.charAt(0).toUpperCase() + widget.id.slice(1).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())}`)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Already Added Widgets Section */}
            {AVAILABLE_WIDGETS.filter(widget => isWidgetAdded(widget.id)).length > 0 && (
              <div className="p-3 sm:p-4 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-200 dark:border-green-800/30">
                <h3 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-3">{t('dashboard.widgets.alreadyAdded')}</h3>
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  {AVAILABLE_WIDGETS.filter(widget => isWidgetAdded(widget.id)).map((widget) => (
                    <div
                      key={widget.id}
                      className="flex items-center justify-center sm:justify-start space-x-1 sm:space-x-2 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 px-2 sm:px-3 py-2 sm:py-2 rounded-lg text-xs sm:text-sm min-h-[40px] opacity-70 cursor-not-allowed"
                    >
                      <svg className="w-3 h-3 sm:w-[14px] sm:h-[14px] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span className="truncate text-center sm:text-left">{t(`dashboard.widgets.add${widget.id.charAt(0).toUpperCase() + widget.id.slice(1).replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())}`)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Grid Layout - Mobile Optimized */}
        <div className="w-full">
          <ResponsiveGridLayout
            className="layout"
            layouts={{ 
              lg: layout,
              md: layout,
              sm: layout,
              xs: layout,
              xxs: layout
            }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 3, md: 2, sm: 1, xs: 1, xxs: 1 }}
            rowHeight={160} // Optimized for mobile
            isDraggable={isEditMode}
            isResizable={isEditMode}
            onLayoutChange={handleLayoutChange}
            measureBeforeMount={false}
            useCSSTransforms={true}
            compactType="vertical"
            preventCollision={false}
            margin={[12, 12]} // Optimized margin for mobile
            containerPadding={[4, 4]} // Minimal padding for mobile
            autoSize={true}
            transformScale={1}
          >
          {layout.map((item) => (
            <div key={item.i} className="relative" data-widget-id={item.i}>
              {/* Remove button (only in edit mode) - Mobile Optimized */}
              {isEditMode && (
                <button
                  className="absolute top-1 sm:top-2 right-1 sm:right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-8 h-8 sm:w-6 sm:h-6 flex items-center justify-center z-50 transition-colors shadow-lg touch-manipulation"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    handleRemoveWidget(item.i)
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation()
                  }}
                  onTouchStart={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <XIcon size={14} className="sm:w-3 sm:h-3" />
                </button>
              )}
              
              {/* Widget Content - Mobile Optimized */}
              <div 
                className={`w-full h-full overflow-hidden rounded-lg ${isEditMode ? 'cursor-move' : ''}`}
                style={{ pointerEvents: isEditMode ? 'none' : 'auto' }}
              >
                {renderWidget(item)}
              </div>
            </div>
          ))}
          </ResponsiveGridLayout>
        </div>

        {/* Edit Controls - Mobile Optimized */}
        <div className="flex justify-center items-center mt-6 sm:mt-8 pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700">
          {!isEditMode ? (
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
              <button
                onClick={handleEditMode}
                className="flex items-center justify-center space-x-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm transition-colors w-full sm:w-auto py-2 sm:py-0 touch-manipulation"
              >
                <EditIcon size={14} />
                <span>{t('dashboard.widgets.customizeDashboard')}</span>
              </button>
              
              <div className="hidden sm:block w-px h-4 bg-gray-300 dark:bg-gray-600" />
              
              <button
                onClick={handleTogglePrivacyMode}
                className={`flex items-center justify-center space-x-2 text-sm transition-colors w-full sm:w-auto py-2 sm:py-0 touch-manipulation ${
                  isPrivacyMode 
                    ? 'text-gray-900 hover:text-blue-700 dark:text-gray-600 dark:hover:text-blue-300' 
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
                title={isPrivacyMode ? t('dashboard.widgets.showData') : t('dashboard.widgets.privacyMode')}
              >
                {isPrivacyMode ? <EyeIcon size={14} /> : <EyeOffIcon size={14} />}
                <span>{isPrivacyMode ? t('dashboard.widgets.showData') : t('dashboard.widgets.privacyMode')}</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto">
              <button
                onClick={handleSave}
                className="flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-3 sm:px-3 sm:py-1.5 rounded text-sm transition-colors touch-manipulation"
              >
                <SaveIcon size={14} />
                <span>Save Changes</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center justify-center space-x-2 bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 sm:px-3 sm:py-1.5 rounded text-sm transition-colors touch-manipulation"
              >
                <XIcon size={14} />
                <span>Cancel</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </DashboardProvider>
  )
}

export default DashboardGrid